/**
 * Aim-lead solver — the "lead ghost" gunnery aid (WoWs-Blitz-style intercept marker).
 *
 * 100% CLIENT-ONLY, pure math, ZERO allocation beyond the small returned object. This never touches the
 * simulation, never predicts authority, and never auto-fires: it only computes WHERE a target will be so
 * the renderer can paint a discreet ghost on the water for the player to lead onto manually.
 *
 * The velocity fed in is the target's INTERPOLATED velocity, derived from snapshots (a remote hull moves
 * purely along its heading at `speed` — see packages/sim stepShipBody: `x += cos(h)*speed*dt`), so this is a
 * read of state we already have, not a forward simulation of the enemy.
 */

export interface LeadSolution {
  /** Flight time (s) until the shot and the target meet. */
  tau: number;
  /** Interception POINT in the sim plane (where to aim) — target position advanced by its velocity·tau. */
  x: number;
  y: number;
}

/**
 * Solve the projectile-intercept: a shot leaves the shooter at (sx, sy) NOW travelling at world speed
 * `speed`, and must strike a target currently at (tx, ty) drifting at constant velocity (vx, vy). Returns
 * the interception point + flight time, or `null` when no shot can catch it (the target outruns the ball, or
 * every solution lies in the past).
 *
 * Closed form: let R = target − shooter. We need |R + V·τ| = speed·τ, i.e.
 *   (V·V − speed²)·τ² + 2(R·V)·τ + (R·R) = 0.
 * A stationary target (V = 0) collapses to τ = |R|/speed with the interception point AT the target's current
 * position (the ghost sits on the hull) — exactly what a still target should show.
 */
export function solveIntercept(
  sx: number, sy: number,
  tx: number, ty: number,
  vx: number, vy: number,
  speed: number,
): LeadSolution | null {
  const rx = tx - sx, ry = ty - sy;
  const a = vx * vx + vy * vy - speed * speed;
  const b = 2 * (rx * vx + ry * vy);
  const c = rx * rx + ry * ry;

  let tau: number;
  const EPS = 1e-9;
  if (Math.abs(a) < EPS) {
    // target closing/opening at exactly the ball speed → the quadratic degenerates to linear b·τ + c = 0
    if (Math.abs(b) < EPS) return null;
    tau = -c / b;
  } else {
    const disc = b * b - 4 * a * c;
    if (disc < 0) return null;                 // the ball can never reach it (target too fast, opening away)
    const sq = Math.sqrt(disc);
    const t1 = (-b - sq) / (2 * a);
    const t2 = (-b + sq) / (2 * a);
    // the earliest strike that is in the FUTURE
    if (t1 > EPS && t2 > EPS) tau = Math.min(t1, t2);
    else if (t1 > EPS) tau = t1;
    else if (t2 > EPS) tau = t2;
    else return null;
  }
  if (!(tau > 0) || !Number.isFinite(tau)) return null;
  return { tau, x: tx + vx * tau, y: ty + vy * tau };
}

/** A candidate hull for the lead ghost — its current sim-plane position. */
export interface LeadCandidate { x: number; y: number; }

/**
 * Pick the most ENGAGEABLE target on the broadside currently being aimed: the nearest hull that lies within
 * `maxRange` AND inside the broadside arc (its bearing from the shooter is within `arcHalfAngle` of the gun
 * normal, on the aimed side). `perpX,perpY` is the UNIT broadside normal for the aimed side — the same
 * `heading ± π/2` the server's fireBroadside and the client's aim guide use, so selection stays coherent
 * with where the guns actually point. Returns the candidate index, or -1 if none qualifies. Pure.
 */
export function selectLeadTarget(
  sx: number, sy: number,
  perpX: number, perpY: number,
  targets: readonly LeadCandidate[],
  maxRange: number,
  arcHalfAngle: number,
): number {
  const cosHalf = Math.cos(arcHalfAngle);
  let best = -1;
  let bestD2 = maxRange * maxRange;
  for (let i = 0; i < targets.length; i++) {
    const dx = targets[i]!.x - sx, dy = targets[i]!.y - sy;
    const d2 = dx * dx + dy * dy;
    if (d2 > bestD2) continue;                 // farther than the current best (or out of range) → skip
    const d = Math.sqrt(d2);
    if (d < 1e-3) continue;                     // coincident — no meaningful bearing
    const dot = (dx * perpX + dy * perpY) / d;  // cos(angle between target bearing and the gun normal)
    if (dot < cosHalf) continue;                // outside the broadside arc / on the wrong side
    best = i;
    bestD2 = d2;
  }
  return best;
}
